import { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

export default function ServiceAgreementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('account_id');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sent, setSent] = useState(null);
  const [mode, setMode] = useState('send'); // 'send' or 'in-person'

  const [form, setForm] = useState({
    agreement_date: new Date().toISOString().slice(0, 10),
    customer_name: '',
    start_date: '',
    property_address: '',
    customer_email: '',
    rep_print_name: user?.full_name || '',
    customer_print_name: '',
    customer_title: 'Community Manager',
  });

  const repCanvasRef = useRef(null);
  const custCanvasRef = useRef(null);
  const [repSigned, setRepSigned] = useState(false);
  const [custSigned, setCustSigned] = useState(false);

  // Pre-fill from account
  useEffect(() => {
    if (!accountId) return;
    api.getAccount(accountId).then(acct => {
      if (acct) {
        setForm(f => ({
          ...f,
          customer_name: f.customer_name || acct.property_name || '',
          property_address: f.property_address || [acct.address, acct.city, acct.state, acct.zip].filter(Boolean).join(', ') || '',
          customer_email: f.customer_email || acct.contact_email || '',
        }));
      }
    }).catch(() => {});
  }, [accountId]);

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function formatDateDisplay(dateStr) {
    if (!dateStr) return '_____ day of ____________, 20____';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const day = d.getDate();
      const s = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
      return `${day}${s} day of ${d.toLocaleDateString('en-US', { month: 'long' })}, ${d.getFullYear()}`;
    } catch { return dateStr; }
  }

  function formatSimpleDate(dateStr) {
    if (!dateStr) return '________________';
    try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return dateStr; }
  }

  async function handleSend() {
    if (!form.agreement_date) return setError('Agreement date is required');
    if (!form.customer_name.trim()) return setError('Customer name is required');
    if (!form.start_date) return setError('Start date is required');
    if (!form.property_address.trim()) return setError('Property address is required');
    if (!form.rep_print_name.trim()) return setError('Your printed name is required');
    if (!repSigned) return setError('Your signature is required');

    if (mode === 'send' && !form.customer_email.trim()) return setError('Customer email is required');
    if (mode === 'in-person' && !custSigned) return setError('Customer signature is required');
    if (mode === 'in-person' && !form.customer_print_name.trim()) return setError('Customer printed name is required');

    setError('');
    setSubmitting(true);

    try {
      const repSig = repCanvasRef.current.toDataURL('image/png');

      if (mode === 'send') {
        const result = await api.sendServiceAgreement({
          account_id: accountId,
          agreement_date: form.agreement_date,
          customer_name: form.customer_name,
          start_date: form.start_date,
          property_address: form.property_address,
          customer_email: form.customer_email,
          rep_print_name: form.rep_print_name,
          rep_signature: repSig,
        });
        setSent(result);
        setSuccess(`Agreement sent to ${form.customer_email}! They\u2019ll receive an email with a signing link.`);
      } else {
        // In-person: create + sign in one shot
        const custSig = custCanvasRef.current.toDataURL('image/png');
        const agreement = await api.sendServiceAgreement({
          account_id: accountId,
          agreement_date: form.agreement_date,
          customer_name: form.customer_name,
          start_date: form.start_date,
          property_address: form.property_address,
          customer_email: form.customer_email || '',
          rep_print_name: form.rep_print_name,
          rep_signature: repSig,
        });
        // Now sign it via the public token
        await api.signPublicAgreement(agreement.signing_token, {
          customer_signature: custSig,
          customer_print_name: form.customer_print_name,
          customer_title: form.customer_title,
        });
        setSent({ ...agreement, status: 'signed' });
        setSuccess('Agreement signed! PDF generated.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to process agreement');
    }
    setSubmitting(false);
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {accountId && (
            <button onClick={() => navigate(`/sales/accounts/${accountId}`)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#64748b' }}>
              &larr;
            </button>
          )}
          <h1 style={{ margin: 0 }}>Service Agreement</h1>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', color: '#16a34a', fontSize: 13, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {/* Mode toggle + recipient panel */}
      <div style={{
        background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20,
        marginBottom: 20, maxWidth: 800, margin: '0 auto 20px',
      }}>
        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {[
            { key: 'send', label: 'Send via Email', icon: '&#9993;' },
            { key: 'in-person', label: 'Sign In Person', icon: '&#9997;' },
          ].map(m => (
            <button key={m.key} onClick={() => setMode(m.key)}
              style={{
                flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer',
                background: mode === m.key ? '#0f172a' : '#f8fafc',
                color: mode === m.key ? '#fff' : '#64748b',
                fontSize: 13, fontWeight: 700, transition: 'all 0.2s',
              }}
              dangerouslySetInnerHTML={{ __html: `${m.icon}&nbsp; ${m.label}` }}
            />
          ))}
        </div>

        <div className="sa-field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Customer / Property Name *</label>
            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
              placeholder="Property name" className="sales-input" style={{ width: '100%', background: '#fff' }} />
          </div>
          {mode === 'send' && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Customer Email *</label>
              <input type="email" value={form.customer_email} onChange={e => set('customer_email', e.target.value)}
                placeholder="manager@property.com" className="sales-input" style={{ width: '100%', background: '#fff' }} />
            </div>
          )}
        </div>
      </div>

      {/* Agreement Document */}
      <div className="sa-document" style={{
        background: '#fff', border: '1px solid #d1d5db', padding: '60px 72px',
        maxWidth: 800, margin: '0 auto',
        fontFamily: "'Times New Roman', Times, serif", fontSize: 14, lineHeight: 1.7,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>

        {/* Logo — large, centered */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo-agreement.png" alt="Apollo Towing" style={{ width: 340, maxWidth: '80%' }} />
        </div>

        {/* Title */}
        <h1 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, margin: '0 0 28px', fontFamily: "'Times New Roman', Times, serif" }}>
          SERVICE AGREEMENT
        </h1>

        {/* Intro */}
        <p style={{ margin: '0 0 14px' }}>
          This General Service Agreement (the &ldquo;Agreement&rdquo;) dated this{' '}
          <strong style={{ textDecoration: 'underline' }}>{formatDateDisplay(form.agreement_date)}</strong>
          {' '}is between{' '}
          <input type="text" value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
            placeholder="Customer Name"
            style={{
              border: 'none', borderBottom: '2px solid #0f172a', background: '#fefce8',
              fontFamily: "'Times New Roman', Times, serif", fontSize: 14, fontWeight: 700,
              padding: '2px 6px', minWidth: 200, textDecoration: 'none', outline: 'none',
            }} />
          , herein referred to as &ldquo;Customer&rdquo; and{' '}
          <strong style={{ textDecoration: 'underline' }}>Apollo Towing LLC</strong>
          , herein referred to as &ldquo;Service Provider&rdquo;.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          Service Provider has agreed to provide services to the Customer on the terms and conditions set out in this Agreement, while Customer is of the opinion that Service Provider has the proper and necessary qualifications, experience and abilities to provide services to Customer.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          Therefore in consideration of the matters described above, the receipt and sufficiency of which consideration is hereby acknowledged, the Customer and the Service Provider agree as follows:
        </p>

        <p style={{ margin: '0 0 6px' }}>
          <strong>1. Scope of Work</strong>: The Service Provider is to provide the Customer with the following services (the &ldquo;Services&rdquo;):
        </p>
        <ul style={{ paddingLeft: 40, margin: '0 0 14px' }}>
          <li>Property patrols for removal of any vehicles not following property parking rules.</li>
          <li>Towing is only authorized upon written request by the property manager on duty.</li>
          <li>The services will include any other tasks which the Customer and the Service Provider may agree on.</li>
        </ul>

        <p style={{ margin: '0 0 14px' }}>
          <strong>2. Term of Agreement</strong>: This Agreement will begin on{' '}
          <strong style={{ textDecoration: 'underline' }}>{formatSimpleDate(form.start_date)}</strong>
          {' '}and will remain in full force and effect until either party seeks termination.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>3. Termination</strong>: If either party seeks termination of this Agreement, the terminating party must provide a 30 day written notice to the other party.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>4. Independent Contractor</strong>: The Service Provider is acting as an independent contractor in providing the Services under this Agreement, not as an employee. The parties agree that this Agreement does not create a joint venture or a partnership between them.
        </p>

        <p style={{ margin: '0 0 14px' }}>
          <strong>5. Property Address:</strong>{' '}
          <input type="text" value={form.property_address} onChange={e => set('property_address', e.target.value)}
            placeholder="Full property address" className="sales-input sa-address-input" style={{ width: '70%' }} />
        </p>

        {/* Editable Fields */}
        <div style={{ margin: '24px 0', padding: 16, background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a' }}>
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            Editable Fields
          </div>
          <div className="sa-field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontFamily: 'Arial, sans-serif' }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Agreement Date *</label>
              <input type="date" value={form.agreement_date} onChange={e => set('agreement_date', e.target.value)} className="sales-input" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="sales-input" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div style={{ marginTop: 36, borderTop: '1px solid #e2e8f0', paddingTop: 24 }}>
          <div className="sa-field-row" style={{ display: 'grid', gridTemplateColumns: mode === 'in-person' ? '1fr 1fr' : '1fr', gap: 40 }}>

            {/* Customer signature — in-person only */}
            {mode === 'in-person' && (
              <div>
                <SignaturePad ref={custCanvasRef} label="Customer Signature" onSign={() => setCustSigned(true)} onClear={() => setCustSigned(false)} />
                <div style={{ marginTop: 12, fontFamily: 'Arial, sans-serif' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Print Name *</label>
                  <input type="text" value={form.customer_print_name} onChange={e => set('customer_print_name', e.target.value)}
                    placeholder="Customer name" className="sales-input" style={{ width: '100%' }} />
                </div>
                <div style={{ marginTop: 8, fontFamily: 'Arial, sans-serif' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Title</label>
                  <input type="text" value={form.customer_title} onChange={e => set('customer_title', e.target.value)}
                    className="sales-input" style={{ width: '100%' }} />
                </div>
              </div>
            )}

            {/* Apollo Rep signature */}
            <div style={{ maxWidth: mode === 'in-person' ? 'none' : 300 }}>
              <SignaturePad ref={repCanvasRef} label="Apollo Towing Authorized Representative" onSign={() => setRepSigned(true)} onClear={() => setRepSigned(false)} />
              <div style={{ marginTop: 12, fontFamily: 'Arial, sans-serif' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>Print Name *</label>
                <input type="text" value={form.rep_print_name} onChange={e => set('rep_print_name', e.target.value)}
                  className="sales-input" style={{ width: '100%' }} />
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontFamily: "'Times New Roman', serif" }}>
                Regional Property Manager
              </div>
            </div>
          </div>

          {mode === 'send' && (
            <div style={{ marginTop: 20, padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', fontFamily: 'Arial, sans-serif' }}>
              <div style={{ fontSize: 12, color: '#0369a1' }}>
                The customer will sign on their side after receiving the email.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, maxWidth: 800, margin: '24px auto 0' }}>
        {accountId && (
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/sales/accounts/${accountId}`)}>
            Back to Account
          </button>
        )}
        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', marginBottom: 8 }}>
              {sent.status === 'signed' ? 'Agreement signed!' : `Sent to ${form.customer_email}`}
            </div>
            {sent.status === 'signed' && sent.id && (
              <a href={`/api/service-agreements/${sent.id}/pdf`} target="_blank" rel="noreferrer"
                className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                Download Signed PDF
              </a>
            )}
            {sent.status !== 'signed' && (
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Waiting for customer signature &bull; Agreement #{sent.id}
              </div>
            )}
          </div>
        ) : (
          <button className="btn btn-primary" onClick={handleSend} disabled={submitting}
            style={{ padding: '12px 32px', fontSize: 15 }}>
            {submitting ? 'Processing...' : mode === 'send' ? 'Sign & Send to Customer' : 'Sign & Generate PDF'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Signature Pad ─────────────────────────────────────────────
const SignaturePad = forwardRef(function SignaturePad({ label, onSign, onClear }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => { if (ref) ref.current = canvasRef.current; }, [ref]);

  const getPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  }, []);

  const start = useCallback((e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }, [getPos]);
  const move = useCallback((e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#000'; ctx.lineTo(p.x, p.y); ctx.stroke(); if (!hasDrawn.current) { hasDrawn.current = true; onSign?.(); } }, [getPos, onSign]);
  const stop = useCallback(() => { drawing.current = false; }, []);
  function clear() { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); hasDrawn.current = false; onClear?.(); }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{label} *</span>
        <button type="button" onClick={clear} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>
      </div>
      <canvas ref={canvasRef} width={400} height={120}
        onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
        style={{ width: '100%', height: 80, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'crosshair', touchAction: 'none' }} />
    </div>
  );
});
